// What the API is known to support (oase-admin-portal PAGINATION_API_CONTRACT.md,
// ADR 0007). Every improvement on the API side is a flag flip here, not a change
// to any screen. All flags start unknown/off (PARITY Q4, Q5).

export interface ApiCapabilities {
  /** The documented maximum page size (API-2). Unknown today. */
  maxPageSize?: number
  /** `GET /{res}?_start=0&_end=0` returns a count without rows (API-6). */
  supportsCountOnly: boolean
  /** Endpoints send `Link: rel="next"` / `X-Has-Next-Page` (API-3). */
  supportsNextPageIndicator: boolean
}

export const API_CAPABILITIES: ApiCapabilities = {
  supportsCountOnly: false,
  supportsNextPageIndicator: false
}

/** The request size for "all records" fetches: the published cap, else 500 (PARITY Q5). */
export const DEFAULT_REQUEST_SIZE = 500

/** `X-Total-Count` as a number; `undefined` when absent, empty or unparsable — never 0 by default. */
export function parseTotal(headers: Headers): number | undefined {
  const raw = headers.get('X-Total-Count')
  if (raw === null || raw.trim() === '') return undefined
  const total = Number.parseInt(raw, 10)
  return Number.isNaN(total) || total < 0 ? undefined : total
}

const NEXT_REL = /;\s*rel\s*=\s*"?(?:[^",;]*\s)?next(?:\s[^",;]*)?"?\s*(?:[;,]|$)/i

/**
 * The next-page signal, honoured only when `supportsNextPageIndicator` is on
 * (so an unrelated `Link` header cannot misfire). `X-Has-Next-Page` wins over
 * `Link`; a `Link` header without rel="next" means no next page.
 */
export function parseHasNextPage(
  headers: Headers,
  capabilities: ApiCapabilities = API_CAPABILITIES
): boolean | undefined {
  if (!capabilities.supportsNextPageIndicator) return undefined
  const explicit = headers.get('X-Has-Next-Page')
  if (explicit !== null) {
    const value = explicit.trim().toLowerCase()
    if (value === 'true') return true
    if (value === 'false') return false
  }
  const link = headers.get('Link')
  if (link !== null) return NEXT_REL.test(link)
  return undefined
}
